import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { useTranslation } from "context/TranslationProvider";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert, Spinner } from "reactstrap";

const PickupConfirmationModal = ({ open, onClose, onConfirm, destinationTimezone, order }) => {
  const { t } = useTranslation();

  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const departureDateRaw =
    order?.departureAt ||
    order?.acceptedOffer?.departureAt ||
    order?.acceptedOffer?.offerDetails?.departureAt ||
    order?.accepted_offer_id?.departureAt ||
    order?.accepted_offer_id?.offerDetails?.departureAt ||
    order?.traveler_listing_id?.departureAt ||
    order?.traveler_listing_id?.date ||
    order?.sender_listing_id?.date;

  const minArrivalDate = departureDateRaw && dayjs(departureDateRaw).isValid()
    ? dayjs(departureDateRaw)
    : dayjs();

  const minDateStr = minArrivalDate.isValid() ? minArrivalDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");

  useEffect(() => {
    if (open) {
      setDate("");
      setTime("");
      setLoading(false);
      setError("");
    }
    // eslint-disable-next-line
  }, [open, order]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((date && !time) || (!date && time)) {
      setError(t("page.orders.pickupModal.errorBothOrNeither", "Please provide both date and time, or leave both empty."));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await onConfirm({ expectedArrivalDate: date, expectedArrivalTime: time });
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || t("page.orders.pickupModal.genericError", "Failed to confirm pickup.");
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const orderIdDisplay = order?.orderid || (order?._id ? order._id.slice(-6) : "");
  const tzDisplay = destinationTimezone || "destination";

  return (
    <Modal isOpen={open} toggle={onClose} centered>
      <ModalHeader toggle={onClose} className="border">
        {t("page.orders.pickupModal.title", "Confirm Parcel Pickup")}
      </ModalHeader>
      
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {orderIdDisplay && (
            <p className="text-muted mb-3">
              {t("page.orders.orderId", "Order ID")}: <strong>{orderIdDisplay}</strong>
            </p>
          )}

          <Alert color="info" className="py-2 text-xs mb-3">
            {t("page.orders.pickupModal.note", "Enter when the traveler expects to arrive at the destination in")}{" "}
            <strong>{tzDisplay}</strong> {t("page.orders.pickupModal.timezone", "timezone")}.
          </Alert>

          {error && (
            <Alert color="danger" className="py-2 text-xs mb-3">
              {error}
            </Alert>
          )}

          <FormGroup>
            <Label>
              {t("page.orders.pickupModal.arrivalDateLabel", "Expected Arrival Date")}
            </Label>
            <Input
              type="date"
              value={date}
              min={minDateStr}
              onChange={(e) => setDate(e.target.value)}
            />
            <small className="text-muted mt-1 d-block">
              {t("page.orders.pickupModal.arrivalDateHintOptional", "This field is optional but if you share user can get reminder.")}
            </small>
          </FormGroup>

          <FormGroup className="mb-0">
            <Label>
              {t("page.orders.pickupModal.arrivalTimeLabel", "Expected Arrival Time")}
            </Label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
            <small className="text-muted mt-1 d-block">
              {t("page.orders.pickupModal.arrivalTimeHintOptional", "This field is optional but if you share user can get reminder.")}
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter className="border">
          <Button color="secondary" onClick={onClose} disabled={loading}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading && <Spinner size="sm" className="mr-2" />}
            {t("page.orders.pickupModal.confirmBtn", "Confirm Pickup")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default PickupConfirmationModal;
